import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { reduxForm, Field, formValueSelector, SubmissionError } from 'redux-form';
import { isEmpty } from 'lodash';
import { compose } from 'redux';
import { Form } from 'react-bootstrap';
import cns from 'classnames';
import BasicTextArea from '../../../../../shared/components/formComponents/groups/BasicTextArea';
import BasicText from '../../../../../shared/components/formComponents/groups/BasicText';
import BasicSelect from '../../../../../shared/components/formComponents/groups/BasicSelect';
import BasicToggle from '../../../../../shared/components/formComponents/groups/BasicToggle';
import RawImageUpload from '../../../../../shared/components/formComponents/inputs/RawImageUpload';
import { VerbRichTextEditor } from '../../../../../shared/components/formComponents/VerbRichTextEditor';
import FormFeedback from '../../../../../shared/components/formComponents/FormFeedback';
import { COLLECTION_TYPES, LICENSE_TYPES, DEFAULT_IMAGE_URLS, CREATED_BY_TYPES } from '../../../../../shared/Constants';
import VerbButton, { VERB_BUTTON_TYPE } from '../../../../../shared/components/VerbButton';
import { getCollectionStatus } from '../../../../../shared/actions/Collections';

export function validateCollectionForm(values) {
    const errors = {};
    if (!isEmpty(values.name) && values.name.length > 250) {
        errors.name = <span className={'form-validation-error'}>Name is too long.</span>;
    }
    if (!values.name) {
        errors.name = <span className={'form-validation-error'}>Must include a name.</span>;
    }
    if (!values.permalink) {
        errors.permalink = <span className={'form-validation-error'}>Must include a permalink.</span>;
    }
    if (!values.subtitle) {
        errors.subtitle = <span className={'form-validation-error'}> Must include a subtitle.</span>;
    }
    if (!values.description) {
        errors.description = <span className={'form-validation-error'}>Must include a description.</span>;
    }
    if (!isEmpty(values.permalink) && !values.permalink.match('^[a-zA-Z0-9-]+$')) {
        errors.permalink = <span className={'form-validation-error'}>Only use letters, numbers, and dashes.</span>;
    }

    if (!isEmpty(errors)) {
        throw new SubmissionError({ ...errors, _error: 'Please fix the errors above.' });
    }
}

class CollectionSummaryForm extends PureComponent {
    static propTypes = {
        handleSubmit: PropTypes.func.isRequired,
        submitSucceeded: PropTypes.bool,
        submitFailed: PropTypes.bool,
        onCancel: PropTypes.func,
        submitting: PropTypes.bool,
        languageOptions: PropTypes.array,
        error: PropTypes.string,
        initialValues: PropTypes.object,
        onSubmit: PropTypes.func,
        allBadges: PropTypes.array,
        verbDimensions: PropTypes.array,
        currentCollection: PropTypes.object,
        getFormFieldValue: PropTypes.func,
        companyAdminDisplay: PropTypes.bool
    };

    _makeSaveButtonClickHandler = () => {
        const { handleSubmit, onSubmit } = this.props;
        return handleSubmit((values) => {
            const updatedValues = {
                ...values,
                createdBy: CREATED_BY_TYPES.BY_VERB,
                subscriptionType: LICENSE_TYPES.AVAILABLE_FOR_ALL
            };
            validateCollectionForm(updatedValues);
            return onSubmit({
                values: updatedValues
            });
        });
    };

    render() {
        const {
            submitting,
            onCancel,
            error,
            allBadges = [],
            verbDimensions,
            currentCollection,
            getFormFieldValue,
            companyAdminDisplay
        } = this.props;
        const collectionType = getFormFieldValue('type');
        return (
            <Form inline className={'collection-form'}>
                <Field
                    required
                    name={'name'}
                    label={'Name'}
                    className={'row collection-form-field'}
                    component={BasicText}
                    description={'Primary title displayed on the card'}
                    inputClassName={'col-12 col-sm-6 no-padding'}
                    styleProps={{ horizontal: true, inputStyle: { bordered: true, 'full-width': true } }}
                    disabled={companyAdminDisplay}
                />
                <Field
                    required
                    name={'permalink'}
                    label={'Permalink'}
                    className={'row collection-form-field'}
                    component={BasicText}
                    description={
                        'A short, shareable link to the Path page. Enables users to get to Paths directly like https://app.verb.net/path/giving-feedback'
                    }
                    inputClassName={'col-12 col-sm-6 no-padding'}
                    styleProps={{ horizontal: true, inputStyle: { bordered: true, 'full-width': true } }}
                    disabled={companyAdminDisplay}
                />
                <Field
                    name={'isActive'}
                    className={cns('row collection-form-field', { hidden: companyAdminDisplay })}
                    inputClassName={'col-6'}
                    label={'Active'}
                    disabled={!(currentCollection.contentIds && currentCollection.contentIds.length)}
                    component={BasicToggle}
                    description={'Displays on the search page and within Paths. Hidden until made active.'}
                    styleProps={{ horizontal: true }}
                    id={'collection-summary-form-is-active-toggle'}
                />
                <Field
                    name={'isLocked'}
                    inputClassName={'col-6'}
                    className={cns('row collection-form-field', { hidden: companyAdminDisplay })}
                    label={'Locked'}
                    component={BasicToggle}
                    description={'Displays with a locked icon on the search page and within the Path'}
                    styleProps={{ horizontal: true }}
                    id={'collection-summary-form-is-locked-toggle'}
                />
                <Field
                    name={'isContentProgressionLocked'}
                    className={cns('row collection-form-field', {
                        hidden: collectionType !== COLLECTION_TYPES.COLLAB || companyAdminDisplay
                    })}
                    inputClassName={'col-6'}
                    label={'Content Progression Locked'}
                    component={BasicToggle}
                    description={'Forces user to complete Path activities in the set sequence.'}
                    styleProps={{ horizontal: true }}
                    display={false}
                    id={'collection-summary-form-is-content-progression-locked-toggle'}
                />
                <Field
                    name={'type'}
                    label={'Type'}
                    className={cns('row collection-form-field', { hidden: companyAdminDisplay })}
                    options={[
                        {
                            label: 'CERTIFIED',
                            value: COLLECTION_TYPES.STANDARD
                        },
                        {
                            label: 'STANDARD (No Certificate)',
                            value: COLLECTION_TYPES.MINI
                        },
                        {
                            label: 'COLLABORATIVE',
                            value: COLLECTION_TYPES.COLLAB
                        }
                    ]}
                    component={BasicSelect}
                    description={'Sets whether the Path will be Standard, Certified, or Collaborative'}
                    inputClassName={'col-12 col-sm-6 no-padding'}
                    styleProps={{ horizontal: true, inputStyle: { bordered: true } }}
                />
                <Field
                    name={'badgeId'}
                    label={'Badge For Completion'}
                    options={[...allBadges, { _id: '', name: 'No badge' }]}
                    valueKey={'_id'}
                    labelKey={'name'}
                    isClearable={true}
                    className={'row collection-form-field'}
                    component={BasicSelect}
                    description={
                        'The selected badge will be assigned and displayed when the learner finishes the Path'
                    }
                    inputClassName={'col-12 col-sm-6 no-padding'}
                    styleProps={{ horizontal: true, inputStyle: { bordered: true } }}
                    disabled={companyAdminDisplay}
                />
                <Field
                    name={'dimensionId'}
                    label={'Primary Dimension'}
                    options={verbDimensions}
                    valueKey={'_id'}
                    labelKey={'name'}
                    isClearable={true}
                    className={cns('row collection-form-field', { hidden: companyAdminDisplay })}
                    component={BasicSelect}
                    description={
                        'This is the Dimension the Path will be associated with under Verb Default Framework'
                    }
                    inputClassName={'col-12 col-sm-6 no-padding'}
                    styleProps={{ horizontal: true, inputStyle: { bordered: true } }}
                />
                <Field
                    required
                    name={'subtitle'}
                    label={'Subtitle'}
                    className={'row collection-form-field'}
                    component={BasicTextArea}
                    rows={2}
                    description={'Subtitle displayed on the card.'}
                    inputClassName={'col-12 col-sm-6 no-padding'}
                    styleProps={{ horizontal: true, inputStyle: { bordered: true, 'full-width': true } }}
                    disabled={companyAdminDisplay}
                />
                <div className={'col-12 collection-description-form-field'}>
                    <Field
                        required
                        component={VerbRichTextEditor}
                        name={'description'}
                        label={'Description'}
                        description={'This is an internal field and does not display on the platform.'}
                        className={cns('col-12 collection-form-field')}
                        inputClassName={'col-12 no-padding'}
                        readOnly={companyAdminDisplay}
                        styleProps={{ horizontal: true }}
                    />
                </div>
                <Field
                    name={'adminDescription'}
                    label={'Admin Description'}
                    className={cns('row collection-form-field', { hidden: companyAdminDisplay })}
                    component={BasicTextArea}
                    rows={2}
                    description={'Internal-facing notes that will not be published out to users.'}
                    inputClassName={'col-12 col-sm-6 no-padding'}
                    styleProps={{ horizontal: true, inputStyle: { bordered: true, 'full-width': true } }}
                />
                <Field
                    component={RawImageUpload}
                    name={'thumbnailImageUrl'}
                    label={'Thumbnail Image'}
                    className={'row collection-form-field'}
                    description={'This is the thumbnail of the Path as it will appear on the cards.'}
                    inputClassName={'col-12 col-sm-6'}
                    styleProps={{ horizontal: true }}
                    disabled={companyAdminDisplay}
                />
                <Field
                    component={RawImageUpload}
                    name={'coverImageUrl'}
                    label={'Cover Image'}
                    className={cns('row collection-form-field', { hidden: companyAdminDisplay })}
                    description={'This is the cover for the Path banner on the Home Page and Path Page'}
                    inputClassName={'col-12 col-sm-6'}
                    styleProps={{ horizontal: true }}
                />
                <div className={'col-12'}>
                    <div className={'row'}>
                        <div className={cns('col-12 formButtons', { hidden: companyAdminDisplay })}>
                            <VerbButton
                                fontAwesomeIcon={'fa-save'}
                                textId={'BUTTON_SAVE'}
                                defaultMessage={'SAVE'}
                                onClick={this._makeSaveButtonClickHandler()}
                                disabled={submitting}
                            />
                            <VerbButton
                                buttonType={VERB_BUTTON_TYPE.SECONDARY_DEFAULT}
                                fontAwesomeIcon={'fa-ban'}
                                textId={'BUTTON_CANCEL'}
                                defaultMessage={'CANCEL'}
                                onClick={onCancel}
                                disabled={submitting}
                            />
                        </div>
                        <div className={cns('col-12 formButtons', { hidden: !companyAdminDisplay })}>
                            <VerbButton
                                buttonType={VERB_BUTTON_TYPE.SECONDARY_DEFAULT}
                                fontAwesomeIcon={'fa-ban'}
                                textId={'BUTTON_CANCEL'}
                                defaultMessage={'CANCEL'}
                                onClick={onCancel}
                                disabled={submitting}
                            />
                        </div>
                        <FormFeedback failMsg={error} className={'col-12'} {...this.props} />
                    </div>
                </div>
            </Form>
        );
    }
}

export const CollectionSummaryFormComponent = CollectionSummaryForm;
export const makeMapStateToProps = () => {
    const mapStateToProps = (state, ownProps) => {
        const {
            name,
            subtitle,
            description = '',
            adminDescription = '',
            dimensionId,
            permalink,
            type = COLLECTION_TYPES.MINI,
            isActive = false,
            isLocked = false,
            isContentProgressionLocked = true,
            thumbnailImageUrl = DEFAULT_IMAGE_URLS.LEARNING_ASSET_THUMBNAIL_IMAGE,
            coverImageUrl = DEFAULT_IMAGE_URLS.LEARNING_ASSET_COVER_IMAGE,
            badgeId,
        } = ownProps.currentCollection;

        let initialValues = {
            name,
            subtitle,
            type,
            description,
            adminDescription,
            dimensionId,
            isLocked,
            permalink,
            subscriptionType: LICENSE_TYPES.AVAILABLE_FOR_ALL,
            isActive,
            thumbnailImageUrl,
            coverImageUrl,
            badgeId,
            createdBy: CREATED_BY_TYPES.BY_VERB,
            isContentProgressionLocked
        };

        if (ownProps.copiedCollection && isEmpty(ownProps.currentCollection)) {
            initialValues = { ...ownProps.copiedCollection };
        }

        return {
            getFormFieldValue: (fieldName) => formValueSelector('Collection')(state, fieldName),
            initialValues
        };
    };

    return mapStateToProps;
};

const mapDispatchToProps = (dispatch) => ({
    getCollectionStatus: (params) => getCollectionStatus(params)
});

const CollectionSummaryFormValidation = {
    form: 'Collection',
    destroyOnUnmount: false,
    enableReinitialize: true
};

export default compose(
    connect(makeMapStateToProps, mapDispatchToProps),
    reduxForm(CollectionSummaryFormValidation)
)(CollectionSummaryForm);
